import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FinancialReconciliationSummary } from '../../../financial-reconciliation/models/financial-reconciliation.model';
import { DashboardChannel, DashboardErp, DashboardOrderFlow } from '../../models/dashboard.model';
import {
  channelLabel,
  FlowCell,
  FlowNote,
  FlowQuery,
  flowScope,
  NO_DISPATCH_DEADLINE,
  notApplicableCell,
  notApplicableReason,
  note,
  ofChannelsText,
  orderLink,
  percent,
  reconciliationLink,
  totalCell,
} from '../../utils/dashboard-flow';
import { DashboardFlowCellComponent } from '../dashboard-flow-cell/dashboard-flow-cell.component';

interface OrderFlowLine {
  id: number | null;
  name: string;
  received: FlowCell;
  erp: FlowCell;
  invoiced: FlowCell;
  dispatched: FlowCell;
  payout: FlowCell;
  cancelled: FlowCell;
}

// Valores de um passo por linha: null = o passo não se aplica ao canal.
interface StepValues {
  main: number | null;
  notes: number[];
}

// Fluxo dos pedidos (spec §8): uma linha por canal, colunas = passos. Recebidos → Enviados ao ERP
// (confirmados, com falha, aguardando) → Faturados → Despachados (só canal com prazo) → Repasse
// conferido (da conciliação por canal) → Cancelados. Cada número abre a lista de pedidos naquele passo,
// com o Full incluído, porque o fluxo conta o Full. Passo que não existe mostra "Não se aplica" e o
// motivo; na linha de total, soma só os canais em que existe e diz "de N canais".
@Component({
  selector: 'app-dashboard-order-flow',
  standalone: true,
  imports: [NgTemplateOutlet, DashboardFlowCellComponent],
  host: { class: 'col-12' },
  styleUrls: ['../dashboard-table.scss'],
  template: `
    <div class="card h-100">
      <div class="card-header">
        <h5 class="card-title mb-0">Fluxo dos pedidos</h5>
      </div>
      <div class="card-body">
        @if (empty) {
          <div class="text-center text-muted py-4" data-empty>
            <i class="ti ti-shopping-cart-off d-block mb-2" style="font-size: 1.75rem; opacity: 0.5"></i>
            <p class="mb-3 small">Nenhum pedido no período.</p>
            <button type="button" class="btn btn-sm btn-label-primary" (click)="widenPeriod.emit()">
              Ver últimos 90 dias
            </button>
          </div>
        } @else {
          <div class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr>
                  <th scope="col">Canal</th>
                  <th scope="col" data-col="received">Recebidos</th>
                  <th scope="col" data-col="erp">Enviados ao ERP</th>
                  <th scope="col" data-col="invoiced">Faturados</th>
                  <th scope="col" data-col="dispatched">Despachados</th>
                  @if (showPayout) {
                    <th scope="col" data-col="payout">Repasse conferido</th>
                  }
                  <th scope="col" data-col="cancelled">Cancelados</th>
                </tr>
              </thead>
              <tbody>
                @for (line of lines; track line.id) {
                  <ng-container *ngTemplateOutlet="row; context: { $implicit: line }"></ng-container>
                }
              </tbody>
              @if (total) {
                <tfoot>
                  <ng-container *ngTemplateOutlet="row; context: { $implicit: total }"></ng-container>
                </tfoot>
              }
            </table>
          </div>
        }
      </div>
    </div>

    <ng-template #row let-line>
      <tr [attr.data-row]="line.id ?? 'total'">
        <td><span class="dash-channel-name">{{ line.name }}</span></td>
        <td data-col="received"><app-dashboard-flow-cell [cell]="line.received"></app-dashboard-flow-cell></td>
        <td data-col="erp"><app-dashboard-flow-cell [cell]="line.erp"></app-dashboard-flow-cell></td>
        <td data-col="invoiced"><app-dashboard-flow-cell [cell]="line.invoiced"></app-dashboard-flow-cell></td>
        <td data-col="dispatched"><app-dashboard-flow-cell [cell]="line.dispatched"></app-dashboard-flow-cell></td>
        @if (showPayout) {
          <td data-col="payout"><app-dashboard-flow-cell [cell]="line.payout"></app-dashboard-flow-cell></td>
        }
        <td data-col="cancelled"><app-dashboard-flow-cell [cell]="line.cancelled"></app-dashboard-flow-cell></td>
      </tr>
    </ng-template>
  `,
})
export class DashboardOrderFlowComponent implements OnChanges {
  /** `orderFlow` do snapshot. */
  @Input() rows?: DashboardOrderFlow[] | null;
  @Input() channels?: DashboardChannel[] | null;
  @Input() erp?: DashboardErp | null;
  /** Resumo da conciliação: `byChannel` dá os conciliados e parciais (Repasse conferido). */
  @Input() summary?: FinancialReconciliationSummary | null;
  @Input() period?: { from?: string; to?: string };
  @Output() widenPeriod = new EventEmitter<void>();

  lines: OrderFlowLine[] = [];
  total: OrderFlowLine | null = null;
  empty = true;

  /** Repasse conferido vem do resumo por canal; sem ele (API antiga, carregando) a coluna não aparece. */
  get showPayout(): boolean {
    return !!this.summary?.byChannel;
  }

  ngOnChanges(): void {
    const rows = this.rows ?? [];
    this.empty = rows.reduce((s, r) => s + r.received, 0) === 0;

    // O destino do ERP é da empresa: o motivo vale para todas as linhas.
    const erpReason = notApplicableReason('orderErp', null, this.erp);

    const perRow = rows.map((r) => {
      const channel = this.channels?.find((c) => c.integrationId === r.integrationId);
      const dispatchReason =
        notApplicableReason('dispatch', channel, this.erp) ?? (r.dispatched === null ? NO_DISPATCH_DEADLINE : null);
      const payoutReason = notApplicableReason('payout', channel, this.erp);
      const payout = this.summary?.byChannel?.find((c) => c.integrationId === r.integrationId);
      return {
        row: r,
        dispatch: dispatchReason,
        payoutReason,
        dispatchValues: { main: dispatchReason ? null : (r.dispatched ?? 0), notes: [r.dispatchLate ?? 0] } as StepValues,
        payoutValues: { main: payoutReason ? null : (payout?.reconciled ?? 0), notes: [payout?.partial ?? 0] } as StepValues,
      };
    });

    this.lines = perRow.map((p): OrderFlowLine => {
      const r = p.row;
      const scope = flowScope(this.period, [r.integrationId]);
      return {
        id: r.integrationId,
        name: channelLabel(r.integrationId, this.channels),
        received: { value: r.received, link: orderLink('received', scope), notes: [] },
        erp: erpReason ? notApplicableCell(erpReason) : this.erpCell(r.erpConfirmed, r.erpFailed, r.erpAwaiting, scope),
        invoiced: this.invoicedCell(r.invoiced, r.received, scope),
        dispatched: p.dispatch ? notApplicableCell(p.dispatch) : this.dispatchCell(p.dispatchValues, scope, null),
        payout: p.payoutReason ? notApplicableCell(p.payoutReason) : this.payoutCell(p.payoutValues, scope, null),
        cancelled: { value: r.cancelled, link: orderLink('cancelled', scope), notes: [] },
      };
    });

    if (rows.length < 2) {
      this.total = null;
      return;
    }

    const all = flowScope(this.period, null);
    const sum = (f: (r: DashboardOrderFlow) => number) => rows.reduce((s, r) => s + f(r), 0);
    const received = sum((r) => r.received);
    this.total = {
      id: null,
      name: 'Total',
      received: { value: received, link: orderLink('received', all), notes: [] },
      erp: erpReason
        ? notApplicableCell(null)
        : this.erpCell(sum((r) => r.erpConfirmed), sum((r) => r.erpFailed), sum((r) => r.erpAwaiting), all),
      invoiced: this.invoicedCell(sum((r) => r.invoiced), received, all),
      dispatched: this.totalStep(perRow.map((p) => ({ id: p.row.integrationId, ...p.dispatchValues })), (v, scope, of) =>
        this.dispatchCell(v, scope, of),
      ),
      payout: this.totalStep(perRow.map((p) => ({ id: p.row.integrationId, ...p.payoutValues })), (v, scope, of) =>
        this.payoutCell(v, scope, of),
      ),
      cancelled: { value: sum((r) => r.cancelled), link: orderLink('cancelled', all), notes: [] },
    };
  }

  /**
   * Total de um passo que pode não existir em alguns canais: soma só os aplicáveis e o link leva só
   * esses canais (todos: sem filtro de canal). Nenhum aplicável: "Não se aplica".
   */
  private totalStep(
    values: (StepValues & { id: number })[],
    build: (v: StepValues, scope: FlowQuery, of: string | null) => FlowCell,
  ): FlowCell {
    const { value, of } = totalCell(values.map((v) => v.main));
    if (value === null) return notApplicableCell(null);
    const applicable = values.filter((v) => v.main !== null);
    const partial = of < values.length;
    const scope = flowScope(this.period, partial ? applicable.map((v) => v.id) : null);
    const notes = applicable[0].notes.map((_, i) => applicable.reduce((s, v) => s + v.notes[i], 0));
    return build({ main: value, notes }, scope, ofChannelsText(of, values.length));
  }

  private erpCell(confirmed: number, failed: number, awaiting: number, scope: FlowQuery): FlowCell {
    const notes: FlowNote[] = [
      ...note(failed, ['com falha', 'com falha'], 'danger', orderLink('erpFailed', scope)),
      ...note(awaiting, ['aguardando', 'aguardando'], 'muted', orderLink('erpAwaiting', scope)),
    ];
    return { value: confirmed, link: orderLink('erpConfirmed', scope), notes };
  }

  private invoicedCell(invoiced: number, received: number, scope: FlowQuery): FlowCell {
    const share = received > 0 ? Math.round((invoiced / received) * 1000) / 10 : null;
    return {
      value: invoiced,
      link: orderLink('invoiced', scope),
      notes: share !== null ? [{ text: `${percent(share)} dos recebidos`, link: null, tone: 'muted' }] : [],
    };
  }

  private dispatchCell(v: StepValues, scope: FlowQuery, of: string | null): FlowCell {
    return {
      value: v.main,
      link: orderLink('dispatched', scope),
      of,
      notes: note(v.notes[0], ['atrasado', 'atrasados'], 'danger', orderLink('dispatchLate', scope)),
    };
  }

  private payoutCell(v: StepValues, scope: FlowQuery, of: string | null): FlowCell {
    return {
      value: v.main,
      link: reconciliationLink('reconciled', scope),
      of,
      notes: note(v.notes[0], ['parcial', 'parciais'], 'warning', reconciliationLink('partial', scope)),
    };
  }
}
